import { defineComponent, ref, onMounted, onBeforeUnmount, watch, h } from "vue-demi";
const createProgram = (ctx, vertexShader, fragmentShader) => {
  const program = ctx.createProgram();
  if (!program) {
    throw new Error("Can not create program");
  }
  ctx.attachShader(program, vertexShader);
  ctx.attachShader(program, fragmentShader);
  ctx.linkProgram(program);
  ctx.useProgram(program);
  return program;
};
const compileShader = (ctx, shaderSource, shaderType) => {
  const shader = ctx.createShader(shaderType);
  if (!shader) {
    throw new Error("Can not compile shader");
  }
  ctx.shaderSource(shader, shaderSource);
  ctx.compileShader(shader);
  if (!ctx.getShaderParameter(shader, ctx.COMPILE_STATUS)) {
    console.warn("Shader compile failed with: " + ctx.getShaderInfoLog(shader));
    return null;
  }
  return shader;
};
const getAttribute = (ctx, program, name) => {
  const location = ctx.getAttribLocation(program, name);
  if (location === -1 || location === null) {
    throw `Cannot find attribute ${name}.`;
  }
  return location;
};
const getUniform = (ctx, program, name) => {
  const location = ctx.getUniformLocation(program, name);
  if (location === -1 || location == null) {
    throw `Cannot find uniform ${name}.`;
  }
  return location;
};
const createVertexBuffer = (ctx, data) => {
  const buffer = ctx.createBuffer();
  if (!buffer) {
    throw "Cannot create buffer";
  }
  ctx.bindBuffer(ctx.ARRAY_BUFFER, buffer);
  ctx.bufferData(ctx.ARRAY_BUFFER, data, ctx.STATIC_DRAW);
  return buffer;
};
const planeVertexData = new Float32Array([
  -1,
  1,
  -1,
  -1,
  1,
  1,
  1,
  -1
]);
const defaultVertexSource = `
attribute vec2 position;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}
`;
var shader2d_vue_vue_type_style_index_0_scoped_true_lang = "";
var _export_sfc = (sfc, props) => {
  for (const [key, val] of props) {
    sfc[key] = val;
  }
  return sfc;
};
const fixCanvasSize = (canvas) => {
  canvas.height = canvas.offsetHeight;
  canvas.width = canvas.offsetWidth;
};
const _sfc_main = defineComponent({
  name: "Shader2d",
  props: {
    ctxOptions: { type: Object, default: () => ({}) },
    webgl: { type: String, default: "webgl" },
    vertex: { type: String, default: defaultVertexSource },
    fragment: { type: String, required: true },
    modelValue: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ["ready", "update:modelValue"],
  setup(props, { emit }) {
    const canvas = ref();
    const isError = ref(false);
    let program;
    let ctx;
    let vertexShader;
    let fragmentShader;
    let animationFrameIndex = null;
    const restart = () => {
      if (animationFrameIndex != null) {
        cancelAnimationFrame(animationFrameIndex);
      }
      emit("ready", {
        ctx,
        gl: ctx,
        createVertexBuffer: (data) => createVertexBuffer(ctx, data),
        getUniform: (name) => getUniform(ctx, program, name),
        getAttribute: (name) => getAttribute(ctx, program, name),
        draw(cb) {
          const frame = () => {
            cb();
            animationFrameIndex = requestAnimationFrame(frame);
          };
          frame();
        },
        canvas: canvas.value,
        onResize
      });
    };
    const onResize = () => {
      if (canvas.value) {
        fixCanvasSize(canvas.value);
      }
      if (ctx) {
        ctx.viewport(0, 0, ctx.canvas.width, ctx.canvas.height);
      }
    };
    onMounted(() => {
      if (!canvas.value) {
        return;
      }
      fixCanvasSize(canvas.value);
      ctx = canvas.value.getContext(props.webgl, props.ctxOptions);
      if (!ctx) {
        throw new Error("Webgl is not supported");
      }
      vertexShader = compileShader(ctx, props.vertex, ctx.VERTEX_SHADER);
      fragmentShader = compileShader(ctx, props.fragment, ctx.FRAGMENT_SHADER);
      if (!vertexShader || !fragmentShader) {
        return isError.value = true;
      }
      program = createProgram(ctx, vertexShader, fragmentShader);
      createVertexBuffer(ctx, planeVertexData);
      const positionLocaltion = getAttribute(ctx, program, "position");
      ctx.enableVertexAttribArray(positionLocaltion);
      ctx.vertexAttribPointer(positionLocaltion, 2, ctx.FLOAT, false, 2 * 4, 0);
      emit("update:modelValue", {
        ctx,
        gl: ctx,
        createVertexBuffer: (data) => createVertexBuffer(ctx, data),
        getUniform: (name) => getUniform(ctx, program, name),
        getAttribute: (name) => getAttribute(ctx, program, name),
        canvas: canvas.value,
        onResize
      });
      restart();
      window.addEventListener("resize", onResize);
    });
    onBeforeUnmount(() => {
      window.removeEventListener("resize", onResize);
    });
    watch(() => props.fragment, () => {
      isError.value = false;
      fragmentShader = compileShader(ctx, props.fragment, ctx.FRAGMENT_SHADER);
      if (!vertexShader || !fragmentShader) {
        return isError.value = true;
      }
      program = createProgram(ctx, vertexShader, fragmentShader);
      restart();
    });
    watch(() => props.vertex, () => {
      isError.value = false;
      vertexShader = compileShader(ctx, props.vertex, ctx.VERTEX_SHADER);
      if (!vertexShader || !fragmentShader) {
        return isError.value = true;
      }
      program = createProgram(ctx, vertexShader, fragmentShader);
      restart();
    });
    return () => isError.value ? h({ text: "Shader compilation error" }) : h("canvas", {
      ref: canvas
    });
  }
});
var Shader2DComponent = /* @__PURE__ */ _export_sfc(_sfc_main, [["__scopeId", "data-v-6e714080"]]);
const Shader2D = Shader2DComponent;
const plugin = {
  install(app) {
    app.component("shader-2d", Shader2DComponent);
  }
};
export { Shader2D, plugin };
